<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import {
  getPendingAgents,
  approvePendingAgent,
  rejectPendingAgent,
  getSectors,
  getSectorTeams,
} from 'dashboard/unichat/api/unichat.js';

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const pendingAgents = ref([]);
const loading = ref(false);
const showApproveModal = ref(false);
const currentAgent = ref(null);

// Approve modal state
const selectedRole = ref('agent');
const selectedSectorId = ref('');
const selectedTeamId = ref('');
const sectors = ref([]);
const sectorTeams = ref([]);
const loadingTeams = ref(false);
const approving = ref(false);

const fetchPending = async () => {
  loading.value = true;
  try {
    const { data } = await getPendingAgents(accountId.value);
    pendingAgents.value = Array.isArray(data) ? data : [];
  } catch {
    useAlert('Erro ao carregar usuários pendentes.');
  } finally {
    loading.value = false;
  }
};

const fetchSectors = async () => {
  try {
    const { data } = await getSectors(accountId.value);
    sectors.value = Array.isArray(data) ? data.filter(s => s.active) : [];
  } catch { /* silencioso */ }
};

watch(selectedSectorId, async (sectorId) => {
  selectedTeamId.value = '';
  sectorTeams.value = [];
  if (!sectorId) return;
  loadingTeams.value = true;
  try {
    const { data } = await getSectorTeams(accountId.value, sectorId);
    sectorTeams.value = Array.isArray(data) ? data : [];
  } catch { /* silencioso */ } finally {
    loadingTeams.value = false;
  }
});

onMounted(() => {
  fetchPending();
  fetchSectors();
});

const openApproveModal = (agent) => {
  currentAgent.value = agent;
  selectedRole.value = 'agent';
  selectedSectorId.value = '';
  selectedTeamId.value = '';
  sectorTeams.value = [];
  showApproveModal.value = true;
};

const closeApproveModal = () => {
  showApproveModal.value = false;
  currentAgent.value = null;
};

const confirmApprove = async () => {
  if (!currentAgent.value) return;
  approving.value = true;
  try {
    await approvePendingAgent(accountId.value, currentAgent.value.id, {
      role: selectedRole.value,
      sector_id: selectedSectorId.value || null,
      team_id: selectedTeamId.value || null,
    });
    useAlert(`${currentAgent.value.name} aprovado com sucesso!`);
    closeApproveModal();
    fetchPending();
  } catch {
    useAlert('Erro ao aprovar usuário.');
  } finally {
    approving.value = false;
  }
};

const rejectAgent = async (agent) => {
  if (!confirm(`Rejeitar e remover o cadastro de ${agent.name}?`)) return;
  try {
    await rejectPendingAgent(accountId.value, agent.id);
    useAlert(`Cadastro de ${agent.name} removido.`);
    fetchPending();
  } catch {
    useAlert('Erro ao remover cadastro.');
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    loading-message="Carregando usuários pendentes..."
    :no-records-found="!pendingAgents.length && !loading"
    no-records-message="Nenhum usuário aguardando aprovação."
  >
    <template #header>
      <BaseSettingsHeader
        title="Usuários Pendentes"
        description="Usuários que se cadastraram e aguardam aprovação do administrador."
      >
        <template #count>
          <span v-if="pendingAgents.length" class="text-body-main text-n-slate-11">
            {{ pendingAgents.length }} aguardando
          </span>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="agent in pendingAgents"
          :key="agent.id"
          class="flex justify-between items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <Avatar :name="agent.name" :size="40" hide-offline-status />
            <div class="flex flex-col gap-1">
              <span class="text-heading-3 text-n-slate-12">{{ agent.name }}</span>
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-body-main text-n-slate-11">{{ agent.email }}</span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                  style="background: rgba(234,179,8,0.12); color: #b45309; border: 1px solid rgba(234,179,8,0.3);"
                >
                  ⏳ Aguardando aprovação
                </span>
                <div class="w-px h-3 bg-n-strong rounded-lg" />
                <span class="text-body-main text-n-slate-11 text-xs">
                  Solicitado em {{ formatDate(agent.created_at) }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex gap-2 shrink-0">
            <Button
              v-tooltip.top="'Aprovar'"
              icon="i-lucide-check-circle"
              size="sm"
              @click="openApproveModal(agent)"
            />
            <Button
              v-tooltip.top="'Rejeitar e remover'"
              icon="i-lucide-x-circle"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="rejectAgent(agent)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <!-- Modal de aprovação -->
  <woot-modal v-model:show="showApproveModal" :on-close="closeApproveModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="`Aprovar: ${currentAgent?.name}`"
        header-content="Defina a função e lotação do usuário antes de aprovar."
      />
      <form class="flex flex-col w-full gap-0" @submit.prevent="confirmApprove">

        <!-- Data de solicitação (readonly) -->
        <div class="w-full">
          <label>
            Data de solicitação
            <input type="text" :value="formatDate(currentAgent?.created_at)" disabled />
          </label>
        </div>

        <!-- Email (readonly) -->
        <div class="w-full">
          <label>
            E-mail
            <input type="text" :value="currentAgent?.email" disabled />
          </label>
        </div>

        <!-- Função -->
        <div class="w-full">
          <label>
            Função
            <select v-model="selectedRole">
              <option value="agent">Agente</option>
              <option value="administrator">Administrador</option>
            </select>
          </label>
        </div>

        <!-- Secretaria -->
        <div v-if="sectors.length" class="w-full">
          <label>
            Secretaria <span class="text-xs text-gray-400">(opcional)</span>
            <select v-model="selectedSectorId">
              <option value="">Nenhuma</option>
              <option v-for="s in sectors" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </label>
        </div>

        <!-- Setor -->
        <div v-if="selectedSectorId" class="w-full">
          <label>
            Setor <span class="text-xs text-gray-400">(opcional)</span>
            <select v-model="selectedTeamId" :disabled="loadingTeams">
              <option value="">{{ loadingTeams ? 'Carregando...' : 'Nenhum' }}</option>
              <option v-for="t in sectorTeams" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </label>
        </div>

        <div class="flex justify-end gap-2 py-4">
          <Button
            faded slate type="button"
            label="Cancelar"
            @click="closeApproveModal"
          />
          <Button
            type="submit"
            label="Aprovar usuário"
            :is-loading="approving"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
